import { useRef, useState } from 'react';
import { api } from '@/services/api.config';

/**
 * The onboarding form link for an account still in Application Pending. The endpoint is scoped to
 * the partner that owns the account, so the caller passes that partner's bearer.
 */
export default function useFetchOnboardingAppUrl() {
  const [fetchingOnboardingUrl, setFetchingOnboardingUrl] = useState(false);
  const [onboardingUrl, setOnboardingUrl] = useState<string>();
  // False until a response says otherwise, so the send button never shows for an account whose owning
  // partner emails its own accounts (Partner Emails Account).
  const [isOnboardingEmailAllowed, setIsOnboardingEmailAllowed] = useState(false);
  // The account whose link is wanted now. A response that arrives after the caller moved to another
  // account is dropped rather than published — a stale link here is one a user could copy or email.
  const requestedAccountID = useRef<string | undefined>(undefined);

  async function fetchOnboardingAppUrl(accountID: string, bearer: string) {
    try {
      setFetchingOnboardingUrl(true);
      requestedAccountID.current = accountID;

      const res = await api.fetchOnboardingAppUrl({ VopayAccountID: accountID }, bearer);
      const isCurrentAccount = requestedAccountID.current === accountID;

      // Read before the Success check: the failure for an account without an application carries it too.
      if (isCurrentAccount) setIsOnboardingEmailAllowed(res.CanSendOnboardingEmail ?? false);

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      if (!isCurrentAccount) return res.OnboardingURL;

      setOnboardingUrl(res.OnboardingURL);

      return res.OnboardingURL;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setFetchingOnboardingUrl(false);
    }
  }

  function reset() {
    requestedAccountID.current = undefined;
    setOnboardingUrl(undefined);
    setIsOnboardingEmailAllowed(false);
  }

  return { onboardingUrl, isOnboardingEmailAllowed, fetchingOnboardingUrl, fetchOnboardingAppUrl, reset };
}
